import { useEffect, useState, useCallback } from "react";
import { useRestaurant } from "../../context/restaurant-context";
import { Button } from "../../tabin/components/button";
import { ModalV2 } from "../../tabin/components/modalv2";
import { GoogleAutoCompleteInput } from "../../tabin/components/googleAutoCompleteInput";
import { useCart } from "../../context/cart-context";
import { ECountry, EOrderType } from "../../model/model";
import { Input } from "../../tabin/components/input";
import { useRegister } from "../../context/register-context";

import "./orderTypeModal.scss";
import { OrderScheduleDateTime } from "../../tabin/components/orderScheduleDateTime";
import { toast } from "../../tabin/components/toast";
import { convertCentsToDollars, getIsRestaurantOpen } from "../../util/util";

interface IOrderTypeModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const OrderTypeModal = ({ isOpen, onClose }: IOrderTypeModalProps) => {
    const { restaurant } = useRestaurant();
    const { register } = useRegister();
    const { setDeliveryAddressAndProcessQuote, orderType, setOrderType, tableNumber, orderScheduledAt, updateOrderScheduledAt } = useCart();

    const [orderScheduledAtOriginal, setOrderScheduledAtOriginal] = useState<string | null>(orderScheduledAt);

    const [showSpinner, setShowSpinner] = useState(false);

    const [deliveryGoogleAddressError, setDeliveryGoogleAddressError] = useState("");
    const [lat, setLat] = useState(0);
    const [lng, setLng] = useState(0);
    const [formattedAddress, setFormattedAddress] = useState("");
    const [aptSuite, setAptSuite] = useState("");
    const [streetAddress, setStreetAddress] = useState("");
    const [city, setCity] = useState("");
    const [state, setState] = useState("");
    const [zipCode, setZipCode] = useState("");
    const [country, setCountry] = useState("");
    const [deliveryNotes, setDeliveryNotes] = useState("");

    // --- Helpers -------------------------------------------------------------

    const setOrderTypeIfChanged = (next: EOrderType | undefined) => {
        if (!next) return;
        if (orderType !== next) setOrderType(next);
    };

    const getValidOptionsForCase = (hasTable: boolean): EOrderType[] =>
        hasTable ? [EOrderType.DINEIN, EOrderType.TAKEAWAY] : [EOrderType.PICKUP, EOrderType.DELIVERY];

    const isDeliveryProviderAvailable = () => Boolean(restaurant?.enableOnlineDelivery);

    const pickPreferredForCase = (hasTable: boolean, available: EOrderType[]): EOrderType | undefined => {
        if (hasTable) {
            if (available.includes(EOrderType.DINEIN)) return EOrderType.DINEIN;
            if (available.includes(EOrderType.TAKEAWAY)) return EOrderType.TAKEAWAY;
        } else {
            if (available.includes(EOrderType.DELIVERY)) return EOrderType.DELIVERY;
            if (available.includes(EOrderType.PICKUP)) return EOrderType.PICKUP;
        }
        return undefined;
    };

    // --- Auto-selection / defaults ------------------------------------------

    useEffect(() => {
        if (!register) return;

        const available = (register.availableOrderTypes as EOrderType[]) ?? [];
        const hasTable = tableNumber !== null;
        const validOptions = getValidOptionsForCase(hasTable);
        const filtered = available.filter((opt) => validOptions.includes(opt) && (opt !== EOrderType.DELIVERY || isDeliveryProviderAvailable()));

        // If exactly one valid option → select it.
        if (filtered.length === 1) {
            const single = filtered[0];
            setOrderTypeIfChanged(single);

            // Auto-close rules:
            // - hasTable (DINEIN/TAKEAWAY): close
            // - no table (PICKUP/DELIVERY): close only if PICKUP
            if (hasTable || single === EOrderType.PICKUP) {
                onClose();
            }
            return;
        }

        // If an explicit orderType exists and is still available, respect it.
        if (orderType && filtered.includes(orderType)) return;

        // Otherwise pick preferred default for the case.
        const preferred = pickPreferredForCase(hasTable, filtered);
        if (preferred) {
            setOrderTypeIfChanged(preferred);
            return;
        }

        // Fallback to first available (if any).
        if (filtered.length > 0) {
            setOrderTypeIfChanged(filtered[0]);
        }
    }, [register, restaurant, orderType, tableNumber, onClose, setOrderType]);

    // --- Actions -------------------------------------------------------------

    const onBeginOrdering = async () => {
        setShowSpinner(true);
        try {
            if (orderType === EOrderType.DINEIN || orderType === EOrderType.TAKEAWAY || orderType === EOrderType.PICKUP) {
                onClose();
                return;
            }

            if (orderType === EOrderType.DELIVERY) {
                const deliveryAddressPayload = {
                    street_address: aptSuite ? [streetAddress, aptSuite] : [streetAddress],
                    city,
                    state,
                    zip_code: zipCode,
                    country,
                };

                const result = await setDeliveryAddressAndProcessQuote(deliveryAddressPayload, lat, lng, deliveryNotes);

                if (result.error) {
                    setDeliveryGoogleAddressError(result.error);
                } else {
                    setShowSpinner(false);
                    onClose();
                }
            }
        } catch (e) {
            console.error(e);
        } finally {
            setShowSpinner(false);
        }
    };

    const onSelectedAddress = useCallback((addressComponents: any[], address: string, latVal: number, lngVal: number) => {
        let aptSuiteLocal = "";
        let streetNumber = "";
        let route = "";
        let cityLocal = "";
        let stateLocal = "";
        let zip = "";
        let countryLocal = "";

        addressComponents.forEach((component) => {
            const types = component.types;
            if (types.includes("subpremise")) aptSuiteLocal = `Unit ${component.long_name}`;
            if (types.includes("street_number")) streetNumber = component.long_name;
            if (types.includes("route")) route = component.long_name;
            if (types.includes("locality")) cityLocal = component.long_name;
            if (types.includes("administrative_area_level_1")) stateLocal = component.short_name;
            if (types.includes("postal_code")) zip = component.long_name;
            if (types.includes("country")) countryLocal = component.short_name;
        });

        setAptSuite(aptSuiteLocal);
        setStreetAddress(`${streetNumber} ${route}`.trim());
        setCity(cityLocal);
        setState(stateLocal);
        setZipCode(zip);
        setCountry(countryLocal);
        setLat(latVal);
        setLng(lngVal);
        setFormattedAddress(address);
    }, []);

    if (!restaurant) return <>Loading restaurant...</>;
    if (!register) return <>Loading register...</>;

    const deliveryProviderAvailable = isDeliveryProviderAvailable();
    const totalDeliveryContributionPercentage =
        (restaurant.onlineDeliveryRestaurantContributionPercentage ?? 0) + (restaurant.onlineDeliveryTabinContributionPercentage ?? 0);
    const totalDeliveryContributionStatic =
        (restaurant.onlineDeliveryRestaurantContributionStatic ?? 0) + (restaurant.onlineDeliveryTabinContributionStatic ?? 0);

    const orderTypeTitle = (
        <div className="title mb-1">
            <div className="h3">Select Your Order Type</div>
            <div className="separator-2"></div>
        </div>
    );

    const orderScheduleTitle = (
        <div className="title mb-1">
            <div className="h3">Order Time</div>
            <div className="separator-2"></div>
        </div>
    );

    const onChangeScheduleDateTime = (dateTimeLocalISO: string | null) => {
        updateOrderScheduledAt(dateTimeLocalISO);
    };

    return (
        <ModalV2 padding="24px" isOpen={isOpen} disableClose={true} onRequestClose={onClose}>
            <div className="order-type-wrapper">
                {/* {(orderScheduledAtOriginal == "INVALID" || !orderScheduledAtOriginal) && (
                    <div className={orderScheduledAt == "INVALID" || !orderScheduledAt ? "" : "mb-6"}>
                        <div>
                            {orderScheduleTitle}
                            <OrderScheduleDateTime
                                onChange={onChangeScheduleDateTime}
                                operatingHours={restaurant.operatingHours}
                                preparationTimeInMinutes={restaurant.preparationTimeInMinutes || 10}
                            />
                        </div>
                        {(orderScheduledAt == "INVALID" || !orderScheduledAt) && (
                            <div className="mt-2 text-error">This restaurant is currently closed. Please select a time in the future.</div>
                        )}
                    </div>
                )} */}
                {/* {orderScheduledAt && orderScheduledAt !== "INVALID" && ( */}
                <>
                    <div className="order-type-header mb-3">
                        {orderTypeTitle}

                        {tableNumber === null && register.availableOrderTypes.includes(EOrderType.DELIVERY) && deliveryProviderAvailable && (
                            <div
                                className={`order-type mb-1 ${orderType === EOrderType.DELIVERY ? "selected" : ""}`}
                                onClick={() => setOrderType(EOrderType.DELIVERY)}
                            >
                                <span>Delivery</span>
                                {(totalDeliveryContributionPercentage > 0 || totalDeliveryContributionStatic > 0) && (
                                    <span className={`order-type-free-delivery ${orderType === EOrderType.DELIVERY ? "selected" : ""}`}>
                                        {totalDeliveryContributionPercentage === 100
                                            ? "Free Delivery"
                                            : totalDeliveryContributionPercentage > 0
                                            ? `${totalDeliveryContributionPercentage}% Off Delivery`
                                            : ""}
                                        {totalDeliveryContributionStatic > 0
                                            ? `$${(totalDeliveryContributionStatic / 100).toFixed(0)} Off Delivery`
                                            : ""}
                                        {restaurant.onlineDeliveryContributionMinimumAmount &&
                                            restaurant.onlineDeliveryContributionMinimumAmount > 0 &&
                                            ` Over $${(restaurant.onlineDeliveryContributionMinimumAmount / 100).toFixed(0)}`}
                                    </span>
                                )}
                            </div>
                        )}
                        {tableNumber === null && register.availableOrderTypes.includes(EOrderType.PICKUP) && (
                            <div
                                className={`order-type mb-1 ${orderType === EOrderType.PICKUP ? "selected" : ""}`}
                                onClick={() => setOrderType(EOrderType.PICKUP)}
                            >
                                Pickup
                            </div>
                        )}
                        {tableNumber !== null && register.availableOrderTypes.includes(EOrderType.DINEIN) && (
                            <div
                                className={`order-type mb-1 ${orderType === EOrderType.DINEIN ? "selected" : ""}`}
                                onClick={() => setOrderType(EOrderType.DINEIN)}
                            >
                                Dine In
                            </div>
                        )}
                        {tableNumber !== null && register.availableOrderTypes.includes(EOrderType.TAKEAWAY) && (
                            <div
                                className={`order-type mb-1 ${orderType === EOrderType.TAKEAWAY ? "selected" : ""}`}
                                onClick={() => setOrderType(EOrderType.TAKEAWAY)}
                            >
                                Takeaway
                            </div>
                        )}
                    </div>

                    {orderType === EOrderType.DINEIN && (
                        <div>
                            <div className="text-bold mb-2">Your food will be delivered to</div>
                            <div className="mb-2">{restaurant?.name}</div>
                            <div>Table: {tableNumber}</div>
                        </div>
                    )}

                    {orderType === EOrderType.TAKEAWAY && (
                        <div>
                            <div className="text-bold mb-2">Pickup Address</div>
                            <div className="mb-2">{restaurant?.name}</div>
                            <div>{restaurant?.address.formattedAddress}</div>
                        </div>
                    )}

                    {orderType === EOrderType.PICKUP && (
                        <div>
                            <div className="text-bold mb-2">Pickup Address</div>
                            <div className="mb-2">{restaurant?.name}</div>
                            <div>{restaurant?.address.formattedAddress}</div>
                        </div>
                    )}

                    {orderType === EOrderType.DELIVERY && (
                        <div>
                            <div className="text-bold mb-2">Enter your delivery address:</div>
                            <div className="delivery-address-input">
                                <GoogleAutoCompleteInput country={ECountry.nz} initialValue={""} onSelectedAddress={onSelectedAddress} />
                            </div>
                            <div className="mb-12"></div>
                            {deliveryGoogleAddressError && <div className="text-error mb-2">{deliveryGoogleAddressError}</div>}
                            {formattedAddress && (
                                <>
                                    <div className="mt-5 text-bold">Please confirm your selected address</div>
                                    <div className="mt-1">{formattedAddress}</div>

                                    <div className="mt-4">
                                        <Input
                                            name="deliveryNotes"
                                            label="Instructions for driver (optional)"
                                            placeholder="Eg. Second floor, black door to the right."
                                            value={deliveryNotes}
                                            onChange={(e) => setDeliveryNotes(e.target.value)}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {orderType && (
                        <Button className="mt-4" loading={showSpinner} onClick={onBeginOrdering}>
                            Begin Ordering
                        </Button>
                    )}
                </>
                {/* )} */}
            </div>
        </ModalV2>
    );
};
